import { redirect } from 'react-router-dom'

import { appStore, isApplicationLocked } from '@/store/app'

import { authCache } from '@/services/auth-cache'
import { api, HttpError } from '@/services/api'
import { MeResponse as MeResponseSchema } from '@leangap/types'
import type { MeResponse, User } from '@/types/session'
import { setSentryUser } from '@/lib/sentry'

export type LoaderDataAccount = { user: MeResponse['user'] }

type MeResult =
	| { kind: 'ok'; user: User }
	// The API answered and the cookie is missing/invalid/expired.
	| { kind: 'unauthenticated' }
	// The API could not be reached or did not answer authoritatively: network
	// error (API restarting locally, offline), Heroku router 503 during a dyno
	// restart, 5xx, 429, or a response that failed schema validation. None of
	// these say anything about the session, so they must not sign the user out.
	| { kind: 'unavailable' }

// single-flight promise to dedupe concurrent /me requests from multiple loaders
let pendingMePromise: Promise<MeResult> | null = null

// Set when /me was unavailable and we rendered from the cached user. While set,
// the next loader run (any navigation inside /account) skips the in-memory fast
// path and calls /me again instead of trusting the unverified cache forever.
let needsRevalidation = false

/** Exposed for tests only. */
export function __resetGuardStateForTests() {
	pendingMePromise = null
	needsRevalidation = false
}

async function fetchMe(): Promise<MeResult> {
	try {
		const resp = (await api('/me', undefined, MeResponseSchema)) as MeResponse
		return { kind: 'ok', user: resp.user }
	} catch (e) {
		if (e instanceof HttpError && e.status === 401) return { kind: 'unauthenticated' }
		return { kind: 'unavailable' }
	}
}

function fetchMeSingleFlight(): Promise<MeResult> {
	if (!pendingMePromise) {
		pendingMePromise = fetchMe().finally(() => {
			// clear the pending promise so future calls can retry/fetch anew
			pendingMePromise = null
		})
	}
	return pendingMePromise
}

function clearSession() {
	// Clear stale auth cache to prevent ensureAnon redirect loop
	authCache.clear()
	appStore.reset()
	setSentryUser(null)
}

/**
 * Apply the server's view of the user after a successful /me.
 *
 * On a re-validation the store may already hold edits the user made while the
 * API was unreachable (autosave keeps retrying them every 30s). Do not discard
 * those unless the server says the application is no longer editable, e.g. a
 * submit whose response was lost, or a submission from another device.
 */
function adoptServerUser(serverUser: User): User {
	authCache.set(serverUser)
	const s = appStore.snapshot()
	const keepLocalEdits = !!s.user && s.dirty && !isApplicationLocked(serverUser.application_process)
	if (!keepLocalEdits) appStore.init(serverUser)
	// Set user context for Sentry so errors include user email
	setSentryUser({ id: serverUser._id, email: serverUser.email })
	return appStore.snapshot().user ?? serverUser
}

export async function ensureAnon() {
	const cached = authCache.get() ?? authCache.loadFromStorage()
	if (cached) throw redirect('/account')
	return null
}

export async function ensureAuthed(): Promise<LoaderDataAccount> {
	const s = appStore.snapshot()
	// If user is already in memory (navigation between sections), use it instantly.
	// This only happens after /me has been called at least once this session,
	// unless the last attempt could not reach the API (see needsRevalidation).
	if (s.user && !needsRevalidation) {
		return { user: s.user }
	}

	// On initial load (new tab, hard refresh), always call /me to validate the session.
	// This ensures users with invalid JWTs (e.g., after secret rotation) are logged out immediately.
	// We still load from localStorage to show UI faster while /me is in flight.
	const cached = s.user ?? authCache.get() ?? authCache.loadFromStorage()
	if (cached && !s.user) {
		appStore.init(cached)
	}

	const result = await fetchMeSingleFlight()

	switch (result.kind) {
		case 'ok':
			needsRevalidation = false
			return { user: adoptServerUser(result.user) }

		case 'unauthenticated':
			// The API rejected the cookie: this is the only outcome that ends the session.
			needsRevalidation = false
			clearSession()
			throw redirect('/login')

		case 'unavailable':
			if (!cached) {
				// Nothing to render from; send the user to log in but leave the
				// (possibly valid) cookie alone so a successful login is not needed
				// twice once the API is back.
				throw redirect('/login')
			}
			// Keep the session: the cookie is untouched and still valid as far as
			// we know. If it is not, the very next authenticated request will get
			// a 401 and the api layer's global handler signs the user out.
			needsRevalidation = true
			return { user: appStore.snapshot().user ?? cached }
	}
}
